// src/components/AppLink.tsx
//
// Drop-in replacement for react-router-dom's `<Link>` that detects
// app-origin routes (post-INF-4 dual-origin layout) at render time
// and renders a native `<a>` element so the browser performs a
// full-page navigation. Marketing-side links fall through to the
// standard `<Link>` (soft client-side navigation).
//
// The caller does NOT need to know whether `to` is an app or
// marketing route — this component decides via `isAppRoute(to)`
// from `src/lib/cross-surface.ts`.
//
// Anchor-only props (className, style, target, rel, aria-*,
// onClick, etc.) work identically on both branches. `<Link>`-only
// props (`replace`, `state`, `preventScrollReset`, `relative`,
// `reloadDocument`) are passed through to `<Link>` when the target
// is a marketing route; they are stripped on the `<a>` branch
// (a native anchor has no soft-navigation state to preserve, and
// React would warn about unknown HTML attributes if they were
// spread onto the `<a>`).
//
// See `src/lib/cross-surface.ts` for the underlying
// `APP_ROUTE_PREFIXES` list and the dual-origin design rationale.

import type { ReactNode } from "react";
import { Link, type LinkProps } from "react-router-dom";
import { isAppRoute } from "@/lib/cross-surface";

export type AppLinkProps = LinkProps & {
  to: string;
  children?: ReactNode;
};

export function AppLink({ to, children, ...rest }: AppLinkProps) {
  if (typeof to === "string" && isAppRoute(to)) {
    const {
      replace: _replace,
      state: _state,
      preventScrollReset: _preventScrollReset,
      relative: _relative,
      reloadDocument: _reloadDocument,
      ...anchorProps
    } = rest;
    return (
      <a href={to} {...anchorProps}>
        {children}
      </a>
    );
  }
  return (
    <Link to={to} {...rest}>
      {children}
    </Link>
  );
}
